import React, { useMemo } from "react";
import { useNavigate } from 'react-router-dom';
import { styled, useTheme } from '@mui/material/styles';
import { ROLE_UUID } from '../const/config';
import { useAuth } from '../contexts/AuthContext';
import MuiDrawer from '@mui/material/Drawer';
import {
    IconButton, Divider, List, ListItem,
    ListItemButton, ListItemIcon, ListItemText, Typography,
} from '@mui/material';
import {
    ChevronLeft, ChevronRight,
    Home, AdminPanelSettings, CorporateFare,
    Warning, History,
    FormatListBulleted, ContactMail, Business, Engineering, ElectricBolt
} from '@mui/icons-material';

const drawerWidth = 250;
const DRAWER_BG_COLOR = '#F5F5F5';
const TEXT_ICON_COLOR = '#333333';
const DIVIDER_COLOR = '#CCCCCC';

// ロールが持つアクセス権を配列で定義（ADMINは全てアクセス可能）
const ROLE_ACCESS = {
    [ROLE_UUID.ADMIN]: [ROLE_UUID.ADMIN, ROLE_UUID.VIEWER],
    [ROLE_UUID.APPROVER]: [ROLE_UUID.VIEWER],
    [ROLE_UUID.REQUESTER]: [ROLE_UUID.VIEWER],
    [ROLE_UUID.VIEWER]: [ROLE_UUID.VIEWER],
};

// メニュー定義
const menu_main = [
    { name: 'ホーム', url: '/', icon: Home, role: ROLE_UUID.VIEWER },
    { name: '地震対応', url: '/earthquake_register', icon: Warning, role: ROLE_UUID.VIEWER },
    { name: '地震履歴一覧', url: '/earthquake_list', icon: History, role: ROLE_UUID.VIEWER },
];

const menu_manage = [
    { name: 'ユーザー管理', url: '/mst_user_list', icon: AdminPanelSettings, role: ROLE_UUID.ADMIN },
    { name: '業務管理', url: '/mst_work_list', icon: FormatListBulleted, role: ROLE_UUID.ADMIN },
    { name: '部署名管理', url: '/mst_department_list', icon: CorporateFare, role: ROLE_UUID.ADMIN },
    { name: 'メール送付先管理', url: '/mst_recipient_list', icon: ContactMail, role: ROLE_UUID.ADMIN },
    { name: '連絡先会社管理', url: '/mst_partner_company_list', icon: Business, role: ROLE_UUID.ADMIN },
    { name: '連絡先担当者管理', url: '/mst_partner_list', icon: Engineering, role: ROLE_UUID.ADMIN },
    { name: '発電所管理(水路抜水)', url: '/mst_suiro_extract_required_list', icon: ElectricBolt, role: ROLE_UUID.ADMIN }
];

const menu = [
    { items: menu_main, open_header_label: '', close_header_label: '', role: ROLE_UUID.VIEWER },
    { items: menu_manage, open_header_label: '', close_header_label: '', role: ROLE_UUID.ADMIN },
];

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: '4px 8px 4px 0px',
    backgroundColor: DRAWER_BG_COLOR,
    justifyContent: 'flex-end',
    ...theme.mixins.toolbar, // AppBarの高さに合わせる
}));

// Drawer の styled 定義
const Drawer = styled(MuiDrawer)(({ theme }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
}));

// 権限チェック
const checkRole = (requiredRole, userRole) => {
    // userRoleがROLE_ACCESSに登録されているか確認
    if (!userRole || !ROLE_ACCESS[userRole]) {
        return false;
    }
    return ROLE_ACCESS[userRole].includes(requiredRole);
};

// メニューヘッダー
const MenuHeader = ({ label }) => (
    <Typography
        variant="caption"
        sx={{
            fontSize: '0.8rem',
            color: TEXT_ICON_COLOR,
            display: 'block'
        }}
    >
        {label}
    </Typography>
);

// メニューアイテム
const MenuItemComponent = React.memo(({ item, onClick }) => (
    <ListItemButton
        onClick={onClick}
        sx={[
            {
                minHeight: 48,
                px: 2.5,
                color: TEXT_ICON_COLOR,
            }
        ]}
    >
        <ListItemIcon
            sx={[
                {
                    minWidth: 0,
                    justifyContent: 'center',
                    color: TEXT_ICON_COLOR
                }
            ]}
        >
            <item.icon />
        </ListItemIcon>
        <ListItemText
            primary={item.name}
        />
    </ListItemButton>
));

// メインコンポーネント
const SlideMenu = ({ open, setOpen }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { currentUser, loading } = useAuth();
    const userRole = currentUser?.role_id;

    const handleDrawerClose = () => setOpen(false);
    const handleClick = (url) => navigate(url);

    // メニューアイテムのレンダリング
    const renderMenuItems = useMemo(() => {
        // ロード中またはロールがない場合はメニューをレンダリングしない
        if (loading || !userRole) {
            return null;
        }

        return menu.map((menuGroup, groupIndex) => {
            // グループ全体の権限チェック
            if (!checkRole(menuGroup.role, userRole)) {
                return null;
            }

            // アイテムごとの権限チェック
            const filteredItems = menuGroup.items.filter(item => checkRole(item.role, userRole));

            if (filteredItems.length === 0) {
                return null;
            }

            return (
                <div key={groupIndex}>
                    {groupIndex !== 0 && (
                        <Divider
                            sx={{
                                border: `0.1px solid ${DIVIDER_COLOR}`,
                                margin: '0px 10px'
                            }}
                        />
                    )}

                    {groupIndex !== 0 && (
                        <MenuHeader
                            label={open ? menuGroup.open_header_label : menuGroup.close_header_label}
                            open={open}
                        />
                    )}

                    <List>
                        {filteredItems.map((item, itemIndex) => (
                            <ListItem
                                key={itemIndex}
                                disablePadding
                                sx={{ display: 'block' }}
                            >
                                <MenuItemComponent
                                    item={item}
                                    onClick={() => handleClick(item.url)}
                                />
                            </ListItem>
                        ))}
                    </List>
                </div>
            );
        });
    }, [userRole, open, navigate, loading]);

    return (
        <>
            <Drawer
                variant="temporary"
                open={open}
                onClose={handleDrawerClose}
                ModalProps={{
                    keepMounted: true,
                }}
                sx={{
                    '& .MuiDrawer-paper': {
                        width: drawerWidth,
                        backgroundColor: DRAWER_BG_COLOR,
                        color: TEXT_ICON_COLOR,
                        '&::-webkit-scrollbar': { display: "none" },
                        msOverflowStyle: 'none',
                        scrollbarWidth: 'none'
                    }
                }}
            >
                <DrawerHeader>
                    <IconButton onClick={handleDrawerClose} sx={{ color: TEXT_ICON_COLOR }}>
                        {theme.direction === 'rtl' ? (
                            <ChevronRight />
                        ) : (
                            <ChevronLeft />
                        )}
                    </IconButton>
                </DrawerHeader>
                {renderMenuItems}
            </Drawer>
        </>
    );
};

export default SlideMenu;